import { useMemo } from 'react'
import { useNavigation } from '../../../../../contexts/NavigationContext'
import {
  useGetAllMintsQuery,
  useGetAvailablePairsQuery,
  useGetCompanyBalanceQuery,
  useGetCompanyWhitelistedAddressesQuery,
} from '../../../../../generated/graphql'

export function useMints() {
  const { selectedCompanyId } = useNavigation()

  // GraphQL queries
  const [{ data: mintsData, fetching: mintsLoading }] = useGetAllMintsQuery({
    variables: { companyId: selectedCompanyId || '' },
    pause: !selectedCompanyId,
  })
  const [{ data: pairsData, fetching: pairsLoading }] = useGetAvailablePairsQuery({
    variables: { companyId: selectedCompanyId || '' },
    pause: !selectedCompanyId,
    requestPolicy: 'cache-first',
  })
  const [{ data: balanceData, fetching: balanceLoading }] = useGetCompanyBalanceQuery({
    variables: { companyId: selectedCompanyId || '' },
    pause: !selectedCompanyId,
  })
  const [{ data: addressesData, fetching: addressesLoading }] =
    useGetCompanyWhitelistedAddressesQuery({
      variables: { companyId: selectedCompanyId || '' },
      pause: !selectedCompanyId,
    })

  // Process data
  const availableMints = useMemo(() => {
    const mints = mintsData?.getAllMints || []
    return mints.map((mint) => ({
      id: mint.id,
      symbol: mint.symbol,
      name: mint.name,
      chain: mint.chain,
      image: mint.image,
    }))
  }, [mintsData])

  // Build a lookup: fromMintId -> Set of allowed toMintIds
  const allowedToMintIds = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const pair of pairsData?.getAvailablePairs || []) {
      if (!map.has(pair.fromMint.id)) map.set(pair.fromMint.id, new Set())
      map.get(pair.fromMint.id)!.add(pair.toMint.id)
    }
    return map
  }, [pairsData])

  const getToMintsFor = useMemo(
    () => (fromMintId: string) => {
      if (!fromMintId) return availableMints
      const allowed = allowedToMintIds.get(fromMintId)
      if (!allowed) return []
      return availableMints.filter((m) => allowed.has(m.id))
    },
    [availableMints, allowedToMintIds],
  )

  const availableMintsWithBalance = useMemo(() => {
    if (!balanceData?.getCompanyBalance.holdings) return []
    return availableMints.filter((mint) =>
      balanceData.getCompanyBalance.holdings
        .filter((h) => parseFloat((h.balance || '0').replace(/,/g, '')) > 0)
        .some((h) => h.mint.id === mint.id),
    )
  }, [availableMints, balanceData])

  const withdrawalAddresses = useMemo(() => {
    if (!addressesData?.getCompanyWhitelistedAddresses) return []
    return addressesData.getCompanyWhitelistedAddresses.filter((addr) => addr.status === 'APPROVED')
  }, [addressesData])

  const allWithdrawalAddresses = useMemo(() => {
    if (!addressesData?.getCompanyWhitelistedAddresses) return []
    return addressesData.getCompanyWhitelistedAddresses
  }, [addressesData])

  const isLoading = mintsLoading || pairsLoading || balanceLoading || addressesLoading

  return {
    availableMints,
    availableMintsWithBalance,
    withdrawalAddresses,
    allWithdrawalAddresses,
    getToMintsFor,
    isLoading,
    balanceData,
  }
}
